import { useState, useCallback, useEffect } from "react";
import { useGetFoodQuery } from "../../redux/services/foods/foodAPI ";
import { useGetFoodTypesQuery } from "../../redux/services/foods/foodTypesAPI";
import Food from "../../components/Food";

const Foods = ({ user }) => {
  const { isLoading, isError, error } = useGetFoodQuery();
  const { data: foodTypes } = useGetFoodTypesQuery();
  const [activeTypeId, setActiveTypeId] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);

  useEffect(() => {
    if (!activeTypeId && foodTypes?.length) {
      setActiveTypeId(foodTypes[0].id);
    }
  }, [foodTypes, activeTypeId]);

  const openModal = useCallback((food) => setSelectedFood(food), []);
  const closeModal = useCallback(() => setSelectedFood(null), []);

  if (isLoading) {
    return <div className="section-shell py-24 text-center text-muted">Loading menu...</div>;
  }

  if (isError) {
    return <div className="section-shell py-24 text-center text-red-500">Error: {error?.data?.message || error?.message}</div>;
  }

  return (
    <section className="bg-white py-12 sm:py-16">
      <div className="section-shell">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <span className="eyebrow">Restaurant booking</span>
          <h1 className="section-title mt-5">Curated dining menu</h1>
          <p className="section-copy mx-auto">
            Order fresh dishes, explore ingredients, and keep restaurant
            reservations in the same flow as your stay.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start lg:gap-10">
          <div>
            <div className="lg:sticky lg:top-24">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-muted">
                Menu categories
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:pb-0 lg:flex lg:flex-col lg:gap-3">
                {(foodTypes || []).map((type) => {
                  const isActive = activeTypeId === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setActiveTypeId(isActive ? null : type.id)}
                      className={`shrink-0 rounded-2xl border p-3 text-left shadow-sm transition duration-300 ${isActive ? "border-gotrip-200 bg-gotrip-50 shadow-soft" : "border-line bg-white hover:border-gotrip-200 hover:bg-surface"}`}
                      aria-pressed={isActive}
                    >
                      <img
                        src={type.food_img}
                        alt={type.name || "Food category"}
                        className="h-40 w-full rounded-xl object-contain"
                        loading="lazy"
                      />
                      <div className="px-1 py-2">
                        <p className="text-sm font-bold uppercase tracking-wide text-gotrip-700">
                          {type.name}
                        </p>
                        <p className="mt-1 text-xs font-semibold text-muted">
                          {type.foods_type?.length || 0} dishes
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {(foodTypes || [])
              .filter((type) => activeTypeId === type.id)
              .map((type) => (
                <div key={type.id} className="premium-card bg-white p-5 sm:p-7">
                  <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-wide text-gotrip-700">
                        Menu section
                      </p>
                      <h2 className="mt-2 text-2xl font-black tracking-tight text-ink sm:text-3xl">
                        {type.name}
                      </h2>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">
                      Fresh today
                    </span>
                  </div>

                  <div className="mt-6 divide-y divide-line">
                    {(type.foods_type || []).map((food) => (
                      <article
                        key={food.id}
                        className="grid gap-5 py-6 first:pt-0 sm:grid-cols-[10rem_1fr]"
                      >
                        <img
                          src={food.food_img}
                          alt={food.name}
                          className="h-32 w-full rounded-2xl object-contain sm:h-full"
                          loading="lazy"
                        />
                        <div className="flex flex-col justify-center">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <h3 className="text-lg font-black tracking-tight text-ink sm:text-xl">
                                {food.name}
                              </h3>
                              <p className="mt-1.5 text-sm leading-6 text-muted">
                                {(food.food_content || []).map(
                                  (content, idx) => (
                                    <span key={`${content.name}-${idx}`}>
                                      {content.name}
                                      {idx < (food.food_content?.length || 0) - 1 ? ", " : ""}
                                    </span>
                                  ),
                                )}
                              </p>
                            </div>
                            <p className="text-xl font-black text-ink sm:text-2xl">
                              ${food.price}
                            </p>
                          </div>
                          <div className="mt-4 flex flex-col gap-2 sm:mt-3 sm:flex-row">
                            <button
                              type="button"
                              onClick={() => openModal(food)}
                              className="btn-secondary btn-xs flex-1"
                            >
                              View Details
                            </button>

                            {!user && (
                              // <button
                              //   type="button"
                              //   onClick={() => openModal(food)}
                              //   className="btn-primary btn-xs flex-1"
                              // >
                              //   Order Now
                              // </button>
                            // ) : (
                              <a href="/login" className="btn-primary btn-xs flex-1 text-center">
                                Login to Order
                              </a>
                            )}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}

            {!(foodTypes || []).find((type) => type.id === activeTypeId) && (
              <div className="premium-card p-10 text-center">
                <p className="text-lg font-bold text-muted">
                  Select a category to view dishes
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedFood && (
        <Food
          food={selectedFood}
          condition={true}
          user={user}
          onClose={closeModal}
        />
      )}
    </section>
  );
};

export default Foods;